import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";

export type DateGranularity = "week" | "month" | "quarter" | "half_year" | "year";

interface BrandMetricResult {
  kind: string;
  brand_equity: {
    dollar: { brand_equity_sale: number[] };
    percent: { brand_equity_sale: number[] };
  };
  brand_metric: Record<string, (number | null)[]>;
  date: string[];
  sample_size: number;
  total: { brand_equity_sale: number };
}

const getBrandEquityV2 = async ({
  startDate,
  endDate,
  brand,
  category,
  product,
  dateGranularity,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  dateGranularity?: DateGranularity;
}): Promise<BrandMetricResult> => {
  const payload: Record<string, any> = {
    brand,
    category,
    product,
    startDate,
    endDate,
    // Legacy API uses period - map dateGranularity to period for backwards compatibility
    period: dateGranularity === "week" ? "weeks" : "months",
  };

  return axiosApi.get(`/api/v2/brand-equity`, {
    baseURL: INSIGHTS_BACKEND_URL,
    params: payload,
  });
};

const _getBrandEquityNest = async ({
  startDate,
  endDate,
  brand,
  category,
  product,
  dateGranularity,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  dateGranularity?: DateGranularity;
}): Promise<BrandMetricResult> => {
  const params = new URLSearchParams();

  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (dateGranularity) params.append("dateGranularity", dateGranularity);

  return axiosApi.get(`/api/brand-equity?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });
};

// Create the hook
export const useBrandEquity = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  dateGranularity = "week",
  config,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  dateGranularity?: DateGranularity;
  config?: QueryConfig<typeof getBrandEquityV2 | typeof _getBrandEquityNest>;
}) => {
  const endpointType = useModuleEndpoint("brand-equity");

  const queryKey = useMemo(
    () => [
      "brand-equity",
      brand,
      category,
      product,
      startDate,
      endDate,
      dateGranularity,
      endpointType,
    ],
    [brand, category, product, startDate, endDate, dateGranularity, endpointType]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return _getBrandEquityNest({
        brand,
        category,
        product,
        startDate,
        endDate,
        dateGranularity,
      });
    } else {
      return getBrandEquityV2({
        brand,
        category,
        product,
        startDate,
        endDate,
        dateGranularity,
      });
    }
  }, [brand, category, product, startDate, endDate, dateGranularity, endpointType]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
  });
};
